/**
 * adminRefreshBus — tiny pub/sub for cross-tab data refresh signalling (iter163).
 *
 * Any admin mutation handler can emit on a scope (e.g. "bookings", "vendors",
 * "pincodes", "team", "crm") and every `useAutoRefresh({ scope })` hook
 * subscribed to that scope will silently refetch.
 *
 * Example:
 *   import { adminRefreshBus } from "../utils/adminRefreshBus";
 *   await authAxios.put("/api/vendors/admin/VND-123", { status: "approved" });
 *   adminRefreshBus.emit("vendors");          // other hooks refresh
 *   adminRefreshBus.emit("ALL");              // broadcast to every subscriber
 *
 * Design notes:
 *   • Not a context provider — single module-level instance avoids provider-hell
 *     and works across lazily-mounted tabs.
 *   • "ALL" is a magic scope — every listener is notified. Use for "something
 *     major changed, be safe" moments (e.g. role promotion that affects RBAC
 *     across many tabs).
 *   • Listeners run inside try/catch so a bad handler doesn't tear down others.
 */

const _listeners = new Map(); // Map<scope, Set<fn>>

const _add = (scope, fn) => {
  if (!_listeners.has(scope)) _listeners.set(scope, new Set());
  _listeners.get(scope).add(fn);
  return () => {
    const set = _listeners.get(scope);
    if (!set) return;
    set.delete(fn);
    if (set.size === 0) _listeners.delete(scope);
  };
};

export const adminRefreshBus = {
  on(scope, fn) {
    if (typeof scope !== "string" || typeof fn !== "function") return () => {};
    return _add(scope, fn);
  },
  emit(scope) {
    if (typeof scope !== "string") return;
    const direct = _listeners.get(scope);
    const broadcast = scope !== "ALL" ? _listeners.get("ALL") : null;
    const notify = (set) => {
      if (!set) return;
      for (const fn of Array.from(set)) {
        try { fn(scope); } catch (e) {
          // Swallow — one bad listener shouldn't tear down the bus and stop
          // every OTHER subscriber from being notified. Log for debugging.
          console.debug("[adminRefreshBus] listener failed:", e?.message);
        }
      }
    };
    notify(direct);
    notify(broadcast);
    // Special "ALL" scope: notify EVERY listener on EVERY scope
    if (scope === "ALL") {
      for (const [s, set] of _listeners.entries()) {
        if (s === "ALL") continue;
        notify(set);
      }
    }
  },
  _reset() {
    // For tests only — do not call in production code.
    _listeners.clear();
  },
};

export default adminRefreshBus;
